<template>
  <div
    v-if="isOpen"
    ref="modalRef"
    class="about-modal"
    :style="modalStyle"
    @mousedown="bringToFront"
  >
    <!-- Header (draggable) -->
    <div
      ref="headerRef"
      class="modal-header"
      :class="{ flashing: isFlashing }"
      @mousedown="startDrag"
    >
      <span class="modal-title">ABOUT</span>
      <button class="close-btn" @click="close" title="Close"><XMarkIcon class="close-icon" /></button>
    </div>

    <!-- Content -->
    <div class="modal-content">
      <!-- Logo -->
      <div class="logo-section">
        <LogoIcon class="about-logo" />
      </div>

      <!-- Description -->
      <p class="description">
        Real-time transaction visualizer for the MegaETH blockchain.
        Watch blocks form, track gas usage, and explore the fastest EVM chain.
      </p>

      <!-- Divider -->
      <div class="divider"></div>

      <!-- Links Section -->
      <div class="links-section">
        <div class="social-links">
          <!-- Twitter/X -->
          <a
            href="https://x.com/megadotarmy"
            target="_blank"
            rel="noopener noreferrer"
            class="social-link"
          >
            <svg class="social-icon" viewBox="0 0 24 24" aria-hidden="true">
              <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" fill="currentColor"/>
            </svg>
            <span>@megadotarmy</span>
          </a>

          <!-- Tutorial -->
          <button class="social-link tutorial-link" @click="startTutorial">
            <svg class="social-icon" viewBox="0 0 24 24" aria-hidden="true">
              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 17h-2v-2h2v2zm2.07-7.75l-.9.92C13.45 12.9 13 13.5 13 15h-2v-.5c0-1.1.45-2.1 1.17-2.83l1.24-1.26c.37-.36.59-.86.59-1.41 0-1.1-.9-2-2-2s-2 .9-2 2H8c0-2.21 1.79-4 4-4s4 1.79 4 4c0 .88-.36 1.68-.93 2.25z" fill="currentColor"/>
            </svg>
            <span>Tutorial</span>
          </button>
        </div>
      </div>

      <!-- Divider -->
      <div class="divider"></div>

      <!-- Built By -->
      <div class="built-by">
        <span class="built-label">Built for the MegaETH community</span>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onUnmounted } from 'vue';
import { XMarkIconSolid as XMarkIcon, LogoIcon } from '@/components/icons';

// Props
export interface AboutModalProps {
  zIndexBase?: number;
}

const props = withDefaults(defineProps<AboutModalProps>(), {
  zIndexBase: 1000
});

// Emits
const emit = defineEmits<{
  close: [];
  focus: [];
  'start-tutorial': [];
}>();

/**
 * Start the tutorial
 */
const startTutorial = () => {
  emit('start-tutorial');
};

// State
const isOpen = ref(false);

// Position and size
const x = ref(100);
const y = ref(100);
const width = ref(380);
const height = ref(480);
const zIndex = ref(props.zIndexBase);

// Flash animation
const isFlashing = ref(false);

// Dragging state
const isDragging = ref(false);
const dragStartX = ref(0);
const dragStartY = ref(0);
const dragOffsetX = ref(0);
const dragOffsetY = ref(0);

// Refs
const modalRef = ref<HTMLElement>();
const headerRef = ref<HTMLElement>();

// Computed style
const modalStyle = computed(() => ({
  left: `${x.value}px`,
  top: `${y.value}px`,
  width: `${width.value}px`,
  height: `${height.value}px`,
  zIndex: zIndex.value
}));

/**
 * Trigger flash animation on header
 */
const flashHeader = () => {
  isFlashing.value = true;
  setTimeout(() => {
    isFlashing.value = false;
  }, 500);
};

/**
 * Open modal centered on screen
 */
const open = () => {
  const viewportWidth = window.innerWidth;
  const viewportHeight = window.innerHeight;

  x.value = Math.max(20, (viewportWidth - width.value) / 2);
  y.value = Math.max(70, (viewportHeight - height.value) / 2);

  isOpen.value = true;
  flashHeader();
};

/**
 * Close modal
 */
const close = () => {
  isOpen.value = false;
  emit('close');
};

/**
 * Bring modal to front (no flash - flash only on external focus)
 */
const bringToFront = () => {
  emit('focus');
};

/**
 * Set z-index externally
 */
const setZIndex = (newZIndex: number) => {
  zIndex.value = newZIndex;
};

/**
 * Start dragging
 */
const startDrag = (e: MouseEvent) => {
  if (e.target !== headerRef.value && !(headerRef.value?.contains(e.target as Node))) {
    return;
  }

  if ((e.target as HTMLElement).classList.contains('close-btn')) {
    return;
  }

  isDragging.value = true;
  dragStartX.value = e.clientX;
  dragStartY.value = e.clientY;
  dragOffsetX.value = x.value;
  dragOffsetY.value = y.value;

  document.addEventListener('mousemove', handleDrag);
  document.addEventListener('mouseup', stopDrag);

  e.preventDefault();
};

/**
 * Handle drag movement
 */
const handleDrag = (e: MouseEvent) => {
  if (!isDragging.value) return;

  const deltaX = e.clientX - dragStartX.value;
  const deltaY = e.clientY - dragStartY.value;

  let newX = dragOffsetX.value + deltaX;
  let newY = dragOffsetY.value + deltaY;

  const viewportWidth = window.innerWidth;
  const viewportHeight = window.innerHeight;

  newX = Math.max(0, Math.min(newX, viewportWidth - width.value));
  newY = Math.max(0, Math.min(newY, viewportHeight - 50));

  x.value = newX;
  y.value = newY;
};

/**
 * Stop dragging
 */
const stopDrag = () => {
  isDragging.value = false;
  document.removeEventListener('mousemove', handleDrag);
  document.removeEventListener('mouseup', stopDrag);
};

// Cleanup on unmount
onUnmounted(() => {
  document.removeEventListener('mousemove', handleDrag);
  document.removeEventListener('mouseup', stopDrag);
});

// Expose methods for parent component
defineExpose({
  open,
  close,
  setZIndex,
  flashHeader,
  isOpen: () => isOpen.value
});
</script>

<style scoped>
.about-modal {
  position: fixed;
  background: var(--bg-surface);
  border: 1px solid var(--accent-primary);
  display: flex;
  flex-direction: column;
  font-family: Inter, Arial, sans-serif;
  user-select: none;
  box-shadow: var(--shadow-lg);
  border-radius: 8px;
  overflow: hidden;
}

/* Header */
.modal-header {
  height: 44px;
  background: linear-gradient(180deg, var(--bg-elevated) 0%, var(--bg-surface) 100%);
  border-bottom: 1px solid var(--border);
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 0 16px;
  cursor: move;
  flex-shrink: 0;
  transition: background 0.3s;
  position: relative;
}

.modal-header.flashing {
  animation: headerFlash 500ms ease-out;
}

@keyframes headerFlash {
  0% { background: var(--accent-primary); }
  100% { background: linear-gradient(180deg, var(--bg-elevated) 0%, var(--bg-surface) 100%); }
}

.modal-title {
  color: var(--accent-primary);
  font-size: 13px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 2px;
}

.close-btn {
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  background: transparent;
  border: 1px solid var(--error);
  color: var(--error);
  width: 26px;
  height: 26px;
  border-radius: 4px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
  padding: 0;
}

.close-btn:hover {
  background: var(--error);
  color: var(--text-inverse);
}

.close-icon {
  width: 14px;
  height: 14px;
}

/* Content */
.modal-content {
  flex: 1;
  padding: 24px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  overflow-y: auto;
}

/* Logo */
.logo-section {
  display: flex;
  justify-content: center;
}

.about-logo {
  height: 40px;
  width: auto;
  color: var(--accent-primary);
}

/* Description */
.description {
  font-size: 13px;
  line-height: 1.6;
  color: var(--text-secondary);
  text-align: center;
  margin: 0;
}

/* Divider */
.divider {
  width: 60px;
  height: 1px;
  background: var(--border);
}

/* Links Section */
.links-section {
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.social-links {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.social-link {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  background: var(--hover-overlay);
  border: 1px solid var(--border);
  border-radius: 6px;
  color: var(--text-primary);
  text-decoration: none;
  font-size: 13px;
  font-weight: 500;
  transition: all 0.2s;
}

.social-link:hover {
  border-color: var(--accent-primary);
  background: var(--active-overlay);
}

.social-icon {
  width: 20px;
  height: 20px;
  color: var(--accent-primary);
  flex-shrink: 0;
}

/* Tutorial button */
.tutorial-link {
  cursor: pointer;
}

/* Built By */
.built-by {
  margin-top: auto;
  padding-top: 8px;
}

.built-label {
  font-size: 11px;
  color: var(--text-muted);
  letter-spacing: 0.5px;
}
</style>
